import { Controller } from "@hotwired/stimulus"
import { useDebounce } from "stimulus-use"
import { post } from "@rails/request.js"

// Connects to data-controller="analytics"
export default class extends Controller {
  static targets = ["scroll"]
  static debounces = ["onScroll"]
  static values = {
    debug: { type: Boolean, default: true },
    trackOutbound: Object,
  }

  initialize() {
    useDebounce(this)

    this.boundOnScroll = this.onScroll.bind(this)

    if (this.enableOutboundTracking()) {
      this.trackLinks()
    }
  }

  scrollTargetConnected(element) {
    element.addEventListener("scroll", this.boundOnScroll)
  }

  scrollTargetDisconnected(element) {
    element.removeEventListener("scroll", this.boundOnScroll)
  }

  onScroll(event) {
    this.dispatch("scroll-start")

    if (
      event.target &&
      event.target.scrollHeight - Math.ceil(event.target.scrollTop) ===
        event.target.clientHeight
    ) {
      this.dispatch("scroll-end")
    }
  }

  track(event) {
    const type = event.type
    const { name, category, action, label } =
      event?.params?.track?.[type] || event?.params?.track || {}

    if (this.debugValue) {
      console.log("analytics#track", { name, category, action, label })
    }

    if (typeof gtag === "function" && !!(name || category || action || label)) {
      gtag("event", name, {
        category: category,
        action: action,
        label: label,
      })
    }
  }

  // Mixpanel event tracking
  trackEvent(event) {
    // Extracts the event type (e.g., playing or paused)
    const type = event.type.split(":")[1] || event.type
    let param = this.data.get(`track-event-${type}-param`)

    // If the specific param for the event type is not found, fall back to the
    // general param
    if (!param) {
      param = this.data.get("track-event-param")
    }

    // Assuming the param is a JSON string that contains event_type and
    // content_discovery_context
    const { event_type, content_discovery_context } = JSON.parse(param)

    const params = {
      event_type: event_type,
      event_properties: {},
    }

    const metaNames = [
      "mp_$device_id",
      "mp_$distinct_id",
      "mp_$current_url",
      "mp_ip",
      "mp_params",
    ]

    metaNames.forEach(name => {
      const metaTag = document.querySelector(`meta[name="${name}"]`)
      if (metaTag) {
        params["event_properties"][name.split("mp_")[1]] =
          metaTag.getAttribute("content")
      }
    })
    params["event_properties"]["content_discovery_context"] =
      content_discovery_context

    post("/tracks", {
      contentType: "application/json",
      body: { track: params },
    })
  }

  enableOutboundTracking() {
    return (
      typeof this.trackOutboundValue === "object" &&
      (this.trackOutboundValue.category ||
        this.trackOutboundValue.action ||
        this.trackOutboundValue.label)
    )
  }

  trackOutbound({ target: { href } }) {
    this.track({
      params: {
        track: JSON.parse(
          JSON.stringify(this.trackOutboundValue).replace("[href]", href),
        ),
      },
    })
  }

  trackLinks() {
    if (!this.outboundLinks) {
      return
    }

    this.outboundLinks.forEach(link => {
      link.addEventListener("click", event => this.trackOutbound(event))
    })
  }

  get outboundLinks() {
    return this.element.querySelectorAll('a[href^="http"]')
  }
}
